import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Linkedin, ArrowRight, CalendarClock } from "lucide-react";
import PageMeta from "../site/PageMeta";
import Chapter from "../site/Chapter";
import { calendlyUrl, calendlyLinkProps, CALENDLY_BASE_URL } from "../../lib/calendly";

export default function Contact() {
  return (
    <>
      <PageMeta
        title="Contact A4 Assist — Let's Talk"
        description="Reach A4 Assist directly: email sahil@a4assist.com, call +1 718 577 1378, or book a 15-minute call on Calendly."
      />

      <section className="pt-36 pb-12">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <Chapter
            number="00"
            eyebrow="Contact us"
            title={
              <>
                Let's talk about what you{" "}
                <span className="font-serif italic">need</span>.
              </>
            }
            subtitle="Tell us what you're trying to solve — whether that's one open role, an operational bottleneck, or a pile of data no one's had time to touch. The fastest way is a 15-minute call. If email or phone is easier, use whichever one suits you."
          />
        </div>
      </section>

      <section className="pb-24 sm:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Direct contact column */}
          <div className="lg:col-span-5 space-y-5">
            {/* Primary — Book a call */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              data-testid="contact-book-card"
              className="relative rounded-2xl border border-[#00E5FF]/30 bg-gradient-to-br from-[#0B1A33] to-[#071326] p-8 overflow-hidden"
            >
              <div className="absolute -top-24 -right-16 h-56 w-56 rounded-full bg-[#00E5FF]/15 blur-3xl" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold text-[#A3FF5E]">
                  <CalendarClock size={12} /> Fastest
                </div>
                <h3 className="font-display mt-4 text-2xl sm:text-3xl font-semibold text-white leading-tight">
                  Book a 15-minute call.
                </h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                  Grab a slot directly on Sahil's calendar. No forms, no gatekeeping — we'll
                  come prepared to talk about your specific situation.
                </p>
                <a
                  href={calendlyUrl({ pillar: "contact" })}
                  {...calendlyLinkProps}
                  data-testid="contact-book-cta"
                  className="group mt-6 inline-flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#050B14] font-semibold px-6 py-3.5 rounded-md shadow-[0_0_30px_-8px_rgba(0,229,255,0.6)]"
                >
                  Book on Calendly
                  <ArrowRight size={16} className="arrow-shift" />
                </a>
              </div>
            </motion.div>

            {/* Email + Phone */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.08 }}
              className="rounded-2xl border border-slate-800 bg-[#0A1325]/60 p-8"
            >
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#00E5FF]">
                Prefer email or phone?
              </div>
              <div className="mt-5 space-y-4">
                <a
                  href="mailto:sahil@a4assist.com"
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 group"
                >
                  <div className="h-11 w-11 rounded-lg bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20 inline-flex items-center justify-center">
                    <Mail size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-slate-500">Email</div>
                    <div className="text-base text-white group-hover:text-[#00E5FF] font-medium truncate">
                      sahil@a4assist.com
                    </div>
                  </div>
                </a>

                <a
                  href="tel:+17185771378"
                  data-testid="contact-phone-link"
                  className="flex items-center gap-4 group"
                >
                  <div className="h-11 w-11 rounded-lg bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20 inline-flex items-center justify-center">
                    <Phone size={18} />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Phone</div>
                    <div className="text-base text-white group-hover:text-[#00E5FF] font-medium">
                      +1 718 577 1378
                    </div>
                  </div>
                </a>

                <a
                  href="https://www.linkedin.com/in/sahil-batra-97410059/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-linkedin-link"
                  className="flex items-center gap-4 group"
                >
                  <div className="h-11 w-11 rounded-lg bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20 inline-flex items-center justify-center">
                    <Linkedin size={18} />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">LinkedIn</div>
                    <div className="text-base text-white group-hover:text-[#00E5FF] font-medium">
                      A4 Assist
                    </div>
                  </div>
                </a>
              </div>

              <div className="mt-6 pt-5 border-t border-slate-800 text-xs text-slate-500">
                Replies within 24h on business days · NDA on request · No long contracts
              </div>
            </motion.div>
          </div>

          {/* Inline Calendly embed */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            data-testid="contact-calendly-embed"
            className="lg:col-span-7 rounded-2xl border border-slate-800 bg-[#0A1325]/60 overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-[#070F1F]">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span className="h-2 w-2 rounded-full bg-[#A3FF5E] animate-pulse" />
                Live availability
              </div>
              <a
                href={calendlyUrl({ pillar: "contact-embed" })}
                {...calendlyLinkProps}
                className="text-xs text-[#00E5FF] hover:text-white transition-colors inline-flex items-center gap-1"
              >
                Open in Calendly <ArrowRight size={12} />
              </a>
            </div>
            <iframe
              src={`${CALENDLY_BASE_URL}?hide_gdpr_banner=1&background_color=0a1325&text_color=f8fafc&primary_color=00e5ff`}
              title="Book a 15-minute call with A4 Assist"
              className="w-full"
              style={{ height: "780px", border: 0 }}
              loading="lazy"
            />
          </motion.div>
        </div>
      </section>
    </>
  );
}
